/**
 * Interactive Claim Example Component
 * Demonstrates how claim cards work with expandable variations and citations
 * Used in the Consensus Guide Modal for educational purposes
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Users, Sparkles } from 'lucide-react';
import { getModelLogo } from '../../../utils/logoMapping';

interface ModelVariation {
  provider: string;
  model: string;
  modelId: string;
  claimText: string;
  similarityScore: number;
  isReference: boolean;
}

interface Citation {
  url: string;
  domain: string;
  citedByModels: string[];
  isShared: boolean;
}

const EXAMPLE_VARIATIONS: ModelVariation[] = [
  {
    provider: 'OpenAI',
    model: 'GPT-4',
    modelId: 'gpt-4',
    claimText: 'Fresenius Medical Care controls approximately 40% of the global dialysis market',
    similarityScore: 1.0,
    isReference: true
  },
  {
    provider: 'Anthropic',
    model: 'Claude',
    modelId: 'claude',
    claimText: 'Fresenius holds a 40% market share in the worldwide dialysis industry',
    similarityScore: 0.96,
    isReference: false
  },
  {
    provider: 'Google',
    model: 'Gemini',
    modelId: 'gemini',
    claimText: 'The dialysis market is dominated by Fresenius, which accounts for roughly 40% globally',
    similarityScore: 0.94,
    isReference: false
  },
  {
    provider: 'xAI',
    model: 'Grok',
    modelId: 'grok',
    claimText: 'With a 40% share, Fresenius Medical Care is the largest player in global dialysis services',
    similarityScore: 0.91,
    isReference: false
  }
];

const EXAMPLE_EXPLANATION = `All four models converged on the same fact: Fresenius Medical Care holds approximately 40% of the global dialysis market. Despite different phrasing ("controls," "holds," "dominated by," "largest player"), the semantic meaning is identical, resulting in 91-100% similarity scores. This is universal agreement across all models.`;

const EXAMPLE_CITATIONS: Citation[] = [
  {
    url: 'https://www.fresenius.com/investor-relations',
    domain: 'fresenius.com',
    citedByModels: ['GPT-4', 'Claude', 'Gemini'],
    isShared: true
  },
  {
    url: 'https://www.statista.com/healthcare-market-share',
    domain: 'statista.com',
    citedByModels: ['GPT-4', 'Grok'],
    isShared: true
  },
  {
    url: 'https://www.healthcare-analytics.org/dialysis-report-2024',
    domain: 'healthcare-analytics.org',
    citedByModels: ['Claude'],
    isShared: false
  }
];

export const InteractiveClaimExample: React.FC = () => {
  const [variationsExpanded, setVariationsExpanded] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  const [uniqueExpanded, setUniqueExpanded] = useState(false);
  const [explanationVisible, setExplanationVisible] = useState(false);

  const referenceClaim = EXAMPLE_VARIATIONS[0];
  const sharedSources = EXAMPLE_CITATIONS.filter(c => c.isShared);
  const uniqueSources = EXAMPLE_CITATIONS.filter(c => !c.isShared);

  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-6">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-slate-400 uppercase tracking-wide font-medium">Try It: Interactive Example</span>
          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded border border-blue-500/30">
            Fact
          </span>
        </div>
        <p className="text-slate-300 text-sm leading-relaxed">
          Below is a live example claim card. Click the buttons to explore how claims, variations, and citations are displayed:
        </p>
      </div>

      {/* Claim Card */}
      <div className="bg-green-500/5 border border-green-500/30 rounded-lg p-4">
        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-base flex-shrink-0">🟢</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Universal Agreement</span>
              <span className="text-xs text-slate-500">96% average similarity</span>
            </div>

            {/* Reference Claim */}
            <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-3 mb-3">
              <p className="text-slate-200 text-sm leading-relaxed">
                {referenceClaim.claimText}
              </p>
            </div>

            {/* Research Sources - Matching SourcePoolDisplay */}
            <div className="mb-3 rounded-lg border border-slate-700/50 bg-slate-800/20 backdrop-blur-sm">
              {/* Header - Collapsible */}
              <button
                onClick={() => setSourcesExpanded(!sourcesExpanded)}
                className="w-full px-4 py-3 border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-300">
                    Research Sources
                  </h3>
                  <span className="text-xs text-slate-500">
                    (3 sources)
                  </span>
                  <div className="ml-auto flex-shrink-0">
                    {sourcesExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>
                {sourcesExpanded && (
                  <p className="text-xs text-slate-400 mt-1">
                    Citations referenced by models in this claim group
                  </p>
                )}
              </button>

              {/* Shared and Unique Sources - Only show when expanded */}
              {sourcesExpanded && (
                <>
                  {/* Shared Sources Section */}
                  {sharedSources.length > 0 && (
                    <div className="border-b border-slate-700/50">
                      <button
                        onClick={() => setSharedExpanded(!sharedExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm font-medium text-cyan-300">
                            Shared Sources
                          </span>
                          <span className="text-xs text-slate-400">
                            ({sharedSources.length})
                          </span>
                        </div>
                        {sharedExpanded ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                      {sharedExpanded && (
                        <div className="px-4 pb-3 space-y-2">
                          {sharedSources.map((citation, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 p-2.5 rounded-lg border bg-cyan-500/10 border-cyan-500/30"
                            >
                              <ExternalLink className="w-4 h-4 flex-shrink-0 mt-0.5 text-cyan-400" />
                              <div className="flex-1 min-w-0">
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium hover:underline break-all text-cyan-400 hover:text-cyan-300"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  {citation.domain}
                                </a>
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  <Users className="w-3 h-3 text-cyan-400" />
                                  <span className="text-xs text-cyan-300 font-medium">
                                    Cited by {citation.citedByModels.length} models
                                  </span>
                                  {citation.citedByModels.length <= 3 && (
                                    <span className="text-xs text-slate-400">
                                      ({citation.citedByModels.join(', ')})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Unique Sources Section */}
                  {uniqueSources.length > 0 && (
                    <div>
                      <button
                        onClick={() => setUniqueExpanded(!uniqueExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-300">
                            Model-Specific Sources
                          </span>
                          <span className="text-xs text-slate-400">
                            ({uniqueSources.length})
                          </span>
                        </div>
                        {uniqueExpanded ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                      {uniqueExpanded && (
                        <div className="px-4 pb-3 space-y-2">
                          {uniqueSources.map((citation, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 p-2.5 rounded-lg border bg-slate-800/40 border-slate-700/50"
                            >
                              <ExternalLink className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                              <div className="flex-1 min-w-0">
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium hover:underline break-all text-slate-300 hover:text-slate-200"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  {citation.domain}
                                </a>
                                {citation.citedByModels.length > 0 && (
                                  <span className="text-xs text-slate-500 mt-1 block">
                                    {citation.citedByModels[0]}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Model Badges */}
            <div className="mb-3">
              <p className="text-xs text-slate-400 mb-2">
                {EXAMPLE_VARIATIONS.length > 1 ? 'Models in consensus:' : 'Identified by:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_VARIATIONS.map((variation, idx) => {
                  const logoSrc = getModelLogo(variation.modelId, variation.model);
                  return (
                    <div
                      key={idx}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-slate-600/50 rounded-lg"
                      title={`${variation.provider} ${variation.model}`}
                    >
                      <div className="flex items-center gap-2">
                        {logoSrc && (
                          <img
                            src={logoSrc}
                            alt={`${variation.model} logo`}
                            className="w-5 h-5 object-contain flex-shrink-0"
                          />
                        )}
                        <span className="text-xs font-medium text-slate-300">{variation.model}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons - Matching Production */}
            <div className="flex gap-2 flex-wrap">
              {/* Show Variations Button */}
              <button
                onClick={() => setVariationsExpanded(!variationsExpanded)}
                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
              >
                <span>{variationsExpanded ? 'Hide' : 'Show'} 4 variations</span>
                {variationsExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>

              {/* Explain Why Button */}
              <button
                onClick={() => setExplanationVisible(!explanationVisible)}
                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
              >
                <span>{explanationVisible ? '💭 Hide explanation' : '💭 View explanation'}</span>
                {explanationVisible ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            {/* Explanation - Matching Production */}
            {explanationVisible && (
              <div className="mt-3 bg-slate-900/30 rounded p-3 border border-slate-700/50">
                <div className="text-xs text-slate-400 mb-1">💭 Explanation:</div>
                <p className="text-slate-300 text-sm italic">{EXAMPLE_EXPLANATION}</p>
              </div>
            )}

            {/* Expanded Variations */}
            {variationsExpanded && (
              <div className="mt-3 space-y-3 border-t border-slate-700/50 pt-3">
                {EXAMPLE_VARIATIONS.map((variation, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="inline-flex items-center gap-2 px-2 py-1 bg-slate-800/50 border border-slate-600/50 rounded">
                        {(() => {
                          const logoSrc = getModelLogo(variation.modelId, variation.model);
                          return logoSrc ? (
                            <img
                              src={logoSrc}
                              alt={`${variation.model} logo`}
                              className="w-4 h-4 object-contain flex-shrink-0"
                            />
                          ) : (
                            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-[9px] font-bold text-white">
                              {variation.model.charAt(0)}
                            </div>
                          );
                        })()}
                        <span className="text-xs font-medium text-slate-300">{variation.model}</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {variation.isReference
                          ? 'Reference claim'
                          : `${Math.round(variation.similarityScore * 100)}% match`}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {variation.claimText}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Explanation Callout */}
      <div className="mt-4 bg-slate-700/20 rounded-lg p-3 border border-slate-600/30">
        <p className="text-xs text-slate-400 leading-relaxed">
          💡 <strong>Try clicking the buttons above</strong> to see how variations show different phrasings of the same claim,
          and how citations are organized into shared sources (multiple models) and unique sources (single model).
          In the actual interface, clicking model badges navigates to the full response.
        </p>
      </div>
    </div>
  );
};
